import { makeAutoObservable, runInAction } from 'mobx';
import { tablesApi, type SettlementMethod, type TabItemInput } from '@/api/tables';
import { isAbortError, RequestSequencer } from '@/api/requests';
import { normalizeApiError, type ApiError } from '@/lib/errors';
import {
  groupTablesBySection,
  groupTablesByState,
  groupTablesByFloor,
  type FloorSection,
  type TableState,
  tableState,
  type RestaurantTable,
  type TabDetail,
} from '@/types/tables';
import type { CompletedOrder, OrderRound, PaymentSummary } from '@/types/checkout';

/**
 * TableStore (V1 fast-follow) — the restaurant floor and the tab open on each
 * table, following the same server-state discipline as OrderStore
 * (01-architecture.md §4): a status enum with retained data, RequestSequencer
 * stale guards, an epoch so responses from a previous scope cannot land, and
 * reset() on scope change.
 *
 * Realtime integration mirrors OrderStore exactly, because a tab IS an order:
 * the same `orders.{organizationId}` channel and the same two events already
 * describe every change that can move a table. `notifyExternalChange()` is the
 * coalesced socket entrypoint; `reconcileFloor()` is the direct fetch the
 * polling fallback drives.
 *
 * Every mutation re-fetches before resolving. The write responses are not
 * trusted to describe the resulting floor, which is what keeps two devices
 * working the same section consistent.
 */

export type LoadStatus = 'idle' | 'loading' | 'success' | 'refreshing' | 'error';

export interface TabActionResult {
  ok: boolean;
  error?: ApiError;
  /** Set by openTab so the caller can navigate straight into the new tab. */
  orderId?: number;
  /**
   * The round just accepted (V2.2-04).
   *
   * The one thing the caller genuinely cannot re-derive by re-fetching: which
   * lines are NEW. A kitchen ticket built from the whole tab would send round 1
   * to the pass again every time round 2 arrives.
   */
  round?: OrderRound | null;
  /** Settlement only — the authoritative settled order and its cash figures. */
  order?: CompletedOrder | null;
  payment?: PaymentSummary | null;
}

/**
 * Sending a tab to the kitchen is an ORDER status advance — the backend has no
 * per-item firing to call instead.
 */
export const KITCHEN_STATUS = 'preparing';

export interface TableStoreOptions {
  api?: Pick<
    typeof tablesApi,
    'floor' | 'tab' | 'openTab' | 'addItems' | 'requestBill' | 'settle' | 'fire'
  >;
  /** Socket-burst coalesce window (06-realtime.md §4 "short window"). */
  coalesceMs?: number;
}

const DEFAULT_COALESCE_MS = 250;

export class TableStore {
  tables: RestaurantTable[] = [];
  floorStatus: LoadStatus = 'idle';
  floorError: ApiError | null = null;
  /** Epoch ms of the last successful floor fetch (connection badge/tooltip). */
  lastFloorSyncMs: number | null = null;

  /** Branch the loaded floor belongs to; a different branch means a reset. */
  branchId: number | null = null;

  /** The tab currently open in the drawer/sheet, if any. */
  tab: TabDetail | null = null;
  tabTableId: number | null = null;
  tabStatus: LoadStatus = 'idle';
  tabError: ApiError | null = null;

  /** Table ids with an in-flight mutation, for per-card busy state. */
  busyTableIds = new Set<number>();

  private readonly api: NonNullable<TableStoreOptions['api']>;
  private readonly coalesceMs: number;
  private readonly seq = new RequestSequencer();
  private coalesceTimer: ReturnType<typeof setTimeout> | null = null;
  private floorStale = false;
  private reconcileInFlight = false;
  private epoch = 0;

  constructor(options: TableStoreOptions = {}) {
    this.api = options.api ?? tablesApi;
    this.coalesceMs = options.coalesceMs ?? DEFAULT_COALESCE_MS;
    makeAutoObservable<
      TableStore,
      'api' | 'coalesceMs' | 'seq' | 'coalesceTimer' | 'floorStale' | 'reconcileInFlight' | 'epoch'
    >(this, {
      api: false,
      coalesceMs: false,
      seq: false,
      coalesceTimer: false,
      floorStale: false,
      reconcileInFlight: false,
      epoch: false,
    });
  }

  // ── derived ──────────────────────────────────────────────────────────────

  get hasFloorData(): boolean {
    return this.tables.length > 0;
  }

  get sections(): { section: string; tables: RestaurantTable[] }[] {
    return groupTablesBySection(this.tables);
  }

  /**
   * The floor grouped by state — bill requested, then occupied, then free.
   *
   * This is what the floor screen renders: a waiter asks "who is waiting for
   * me", and grouped by section that answer was spread across every heading.
   */
  get stateGroups(): { state: TableState; label: string; tables: RestaurantTable[] }[] {
    return groupTablesByState(this.tables);
  }

  /**
   * The same floor, split by ROOM first and by state within each room.
   *
   * This is what the floor screen renders. `stateGroups` above is kept because
   * the summary counts and the register's table picker read the floor flat —
   * they answer "how many are waiting", which has no room in it.
   */
  get floorSections(): FloorSection[] {
    return groupTablesByFloor(this.tables);
  }

  get occupiedCount(): number {
    return this.tables.filter((t) => tableState(t) !== 'free').length;
  }

  get billRequestedCount(): number {
    return this.tables.filter((t) => tableState(t) === 'bill_requested').length;
  }

  get freeCount(): number {
    return this.tables.filter((t) => tableState(t) === 'free').length;
  }

  isBusy(tableId: number): boolean {
    return this.busyTableIds.has(tableId);
  }

  findTable(tableId: number): RestaurantTable | null {
    return this.tables.find((t) => t.id === tableId) ?? null;
  }

  // ── scope ────────────────────────────────────────────────────────────────

  setBranch(branchId: number | null): void {
    if (this.branchId === branchId) return;
    this.reset();
    this.branchId = branchId;
  }

  reset(): void {
    this.epoch += 1;
    this.seq.abortAll();
    if (this.coalesceTimer !== null) {
      clearTimeout(this.coalesceTimer);
      this.coalesceTimer = null;
    }
    this.tables = [];
    this.floorStatus = 'idle';
    this.floorError = null;
    this.lastFloorSyncMs = null;
    this.branchId = null;
    this.closeTab();
    this.busyTableIds = new Set();
    this.floorStale = false;
    this.reconcileInFlight = false;
  }

  // ── coalesced realtime entrypoint ────────────────────────────────────────

  /**
   * Socket-event hint. A tab is an order, so the existing OrderStatusChanged /
   * NewOrderReceived events already cover every transition a table can make;
   * this store needs no channel of its own.
   *
   * Payloads are treated as invalidation hints only — the floor is re-fetched
   * over authenticated HTTP rather than patched from the event.
   */
  notifyExternalChange(): void {
    this.floorStale = true;
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.coalesceTimer !== null || this.reconcileInFlight) return;
    this.coalesceTimer = setTimeout(() => {
      this.coalesceTimer = null;
      void this.flushStale();
    }, this.coalesceMs);
  }

  private async flushStale(): Promise<void> {
    if (!this.floorStale) return;
    this.floorStale = false;

    const refreshes: Promise<unknown>[] = [this.reconcileFloor()];
    // Only the tab actually on screen re-fetches; there is no cache to go
    // stale behind it.
    if (this.tabTableId !== null) refreshes.push(this.loadTab(this.tabTableId, { quiet: true }));

    await Promise.allSettled(refreshes);
    if (this.floorStale) this.scheduleFlush();
  }

  // ── floor ────────────────────────────────────────────────────────────────

  async loadFloor(): Promise<void> {
    await this.reconcileFloor();
  }

  /**
   * Fetches the floor and REPLACES it (the response is authoritative). Returns
   * false on failure so the polling fallback can back off. Stale responses —
   * superseded, aborted, or from a previous scope epoch — never land.
   */
  async reconcileFloor(): Promise<boolean> {
    if (this.branchId === null) return false;

    const startEpoch = this.epoch;
    const branchId = this.branchId;
    const ticket = this.seq.begin('tables:floor');
    this.reconcileInFlight = true;
    this.floorStatus = this.hasFloorData ? 'refreshing' : 'loading';

    try {
      const tables = await this.api.floor(branchId, { signal: ticket.signal });
      if (!ticket.isCurrent() || this.epoch !== startEpoch) return false;
      runInAction(() => {
        this.tables = tables;
        this.floorStatus = 'success';
        this.floorError = null;
        this.lastFloorSyncMs = Date.now();
      });
      return true;
    } catch (e) {
      if (isAbortError(e) || !ticket.isCurrent() || this.epoch !== startEpoch) return false;
      runInAction(() => {
        this.floorError = normalizeApiError(e);
        this.floorStatus = 'error';
      });
      return false;
    } finally {
      runInAction(() => {
        this.reconcileInFlight = false;
      });
      if (this.floorStale) this.scheduleFlush();
    }
  }

  // ── tab detail ───────────────────────────────────────────────────────────

  /**
   * Loads one table's open tab. `quiet` keeps the currently rendered tab on
   * screen during a background refresh instead of flashing a spinner.
   */
  async loadTab(tableId: number, options: { quiet?: boolean } = {}): Promise<void> {
    const startEpoch = this.epoch;
    const ticket = this.seq.begin('tables:tab');

    runInAction(() => {
      this.tabTableId = tableId;
      this.tabStatus = options.quiet && this.tab ? 'refreshing' : 'loading';
      if (!options.quiet) this.tabError = null;
    });

    try {
      const tab = await this.api.tab(tableId, { signal: ticket.signal });
      if (!ticket.isCurrent() || this.epoch !== startEpoch) return;
      runInAction(() => {
        this.tab = tab;
        this.tabStatus = 'success';
        this.tabError = null;
      });
    } catch (e) {
      if (isAbortError(e) || !ticket.isCurrent() || this.epoch !== startEpoch) return;
      runInAction(() => {
        this.tabError = normalizeApiError(e);
        this.tabStatus = 'error';
      });
    }
  }

  closeTab(): void {
    this.seq.abort('tables:tab');
    this.tab = null;
    this.tabTableId = null;
    this.tabStatus = 'idle';
    this.tabError = null;
  }

  // ── mutations ────────────────────────────────────────────────────────────

  /**
   * Seat a table.
   *
   * A 409 means another device got there first, which is not really an error —
   * the floor is simply behind. The floor is refreshed either way so the UI
   * lands on the truth rather than on an optimistic guess.
   */
  async openTab(
    tableId: number,
    body: {
      covers?: number;
      notes?: string;
      daySessionId?: number | null;
      /**
       * The first round, sent WITH the seating (V2.2-04).
       *
       * One request, so the table claim, the pricing, the stock movement and
       * round 1 are one transaction upstream. Opening then appending left a gap
       * in which a tab existed with no kitchen ticket and no items.
       */
      items?: TabItemInput[];
      clientOrderId?: string;
      promotionCodes?: string[];
      customerId?: number;
    } = {},
  ): Promise<TabActionResult> {
    return this.withBusy(tableId, async () => {
      const result = await this.api.openTab(tableId, body);
      await this.reconcileFloor();
      return { ok: true, orderId: result.orderId ?? undefined, round: result.round };
    });
  }

  /**
   * Add a round to an open tab.
   *
   * `clientRoundId` is the upstream idempotency key. We still never retry a
   * transport failure automatically: the refreshed tab lets the waiter confirm
   * what landed, and a deliberate retry with the SAME key returns that original
   * round instead of adding it twice.
   */
  async addItems(
    tableId: number,
    orderId: number,
    items: TabItemInput[],
    clientRoundId?: string,
  ): Promise<TabActionResult> {
    return this.withBusy(tableId, async () => {
      const result = await this.api.addItems(orderId, items, { clientRoundId });
      await Promise.all([this.reconcileFloor(), this.loadTab(tableId, { quiet: true })]);
      return { ok: true, orderId, round: result.round };
    });
  }

  /**
   * Send the tab to the kitchen.
   *
   * There is NO per-item firing upstream — no fired flag, no course model, no
   * per-line kitchen state. The only kitchen signal that exists is the order
   * status, so this advances the whole tab to `preparing` through the same
   * endpoint the queue uses. Callers must not draw per-item fired state on top
   * of it.
   *
   * Gated on `tables.fire` upstream, not `sell`, so a waiter CAN send their
   * own table -- but the route resolves a tab first, so it is not general
   * queue advance.
   */
  async fireToKitchen(tableId: number, orderId: number): Promise<TabActionResult> {
    return this.withBusy(tableId, async () => {
      await this.api.fire(orderId);
      await Promise.all([this.reconcileFloor(), this.loadTab(tableId, { quiet: true })]);
      return { ok: true };
    });
  }

  /** Flag that the guest asked for the bill. Idempotent upstream. */
  async requestBill(tableId: number, orderId: number): Promise<TabActionResult> {
    return this.withBusy(tableId, async () => {
      await this.api.requestBill(orderId);
      await Promise.all([this.reconcileFloor(), this.loadTab(tableId, { quiet: true })]);
      return { ok: true };
    });
  }

  /**
   * Settle a tab and free its table.
   *
   * `method` is a manually verified label, exactly as at checkout — no
   * gateway, no capture. Requires `sell` upstream, so a waiter-only account
   * gets 403 here by design and the UI hides the control for them.
   */
  async settle(
    tableId: number,
    orderId: number,
    method: SettlementMethod,
    options: { notes?: string; cashReceived?: number } = {},
  ): Promise<TabActionResult> {
    return this.withBusy(tableId, async () => {
      const result = await this.api.settle(orderId, method, options);
      await this.reconcileFloor();
      runInAction(() => {
        // The table is free now; keeping the settled tab on screen would
        // invite a second settle attempt against a closed order.
        if (this.tabTableId === tableId) this.closeTab();
      });
      // The settled order and its server-computed cash figures come BACK to the
      // caller. Discarding them, as this used to, is why closing a tab could
      // never produce a receipt.
      return { ok: true, orderId, order: result.order ?? null, payment: result.payment };
    });
  }

  private async withBusy(
    tableId: number,
    action: () => Promise<TabActionResult>,
  ): Promise<TabActionResult> {
    if (this.busyTableIds.has(tableId)) {
      return { ok: false, error: { status: 0, message: 'That table is already updating.' } };
    }

    runInAction(() => {
      this.busyTableIds = new Set(this.busyTableIds).add(tableId);
    });

    try {
      return await action();
    } catch (e) {
      const error = normalizeApiError(e);
      // Even a failed mutation may have landed, so the floor is refreshed
      // before the caller decides what to show.
      await this.reconcileFloor();
      return { ok: false, error };
    } finally {
      runInAction(() => {
        const next = new Set(this.busyTableIds);
        next.delete(tableId);
        this.busyTableIds = next;
      });
    }
  }
}
